/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { PCView, SPView } from "components/common/MediaQuery";
import { format } from "date-fns";
import { useI18n } from "hooks/i18n/i18n";
import { withNoTranslate } from "i18n/noTranslate";
import { useState } from "react";
import { useHistory } from "react-router";
import { AgreementCheckbox } from "ui/AgreementCheckbox";
import { AmountSummaryTable } from "ui/AmountSummaryTable";
import { BreadcrumbNavigation } from "ui/BreadcrumbNavigation";
import { DetailsTable } from "ui/DetailsTable";
import { MobileBreadcrumbNavigation } from "ui/MobileBreadcrumbNavigation";
import { SectionHeading } from "ui/SectionHeading";
import { Stepper } from "ui/Stepper";
import { SubContentsLayout } from "ui/SubContentsLayout";
import { getGMOToken } from "utility/gmo";

import { ResaleApplicationPaymentMethod } from "./ResaleApplicationPaymentMethod";
import { ResaleApplicationSubmissionAction } from "./ResaleApplicationSubmissionAction";

import type { ComponentProps, FC, FormEvent } from "react";

export const ResaleApplicationConfirmation: FC<{
  eventId: string;
  ticketId: string;

  creditCard: ComponentProps<
    typeof ResaleApplicationPaymentMethod
  >["creditCard"];
  desiredQuantity: number;
  eventOpenAt: number;
  eventStartAt: number;
  eventTitle: string;
  purchaseFeeAmount: number;
  ticketName: string;
  ticketPriceAmount: number;
}> = ({
  eventId,
  ticketId,

  creditCard,
  desiredQuantity,
  eventOpenAt,
  eventStartAt,
  eventTitle,
  purchaseFeeAmount,
  ticketName,
  ticketPriceAmount,
}) => {
  const history = useHistory();
  const { t, i18n } = useI18n();
  const classes = styles();

  const [isRegisteredCreditCardSelected, setIsRegisteredCreditCardSelected] =
    useState(true);
  const [isAgreed, setIsAgreed] = useState(false);
  const pageTitle = t("event.resale.entry.confirmationTitle");
  const eventOpenAtDate = new Date(eventOpenAt);
  const weekdayFormatter = new Intl.DateTimeFormat(
    i18n.language.replace("_", "-"),
    { weekday: "short" }
  );
  const eventDateTime = `${format(eventOpenAtDate, "yyyy/MM/dd")} (${weekdayFormatter.format(
    eventOpenAtDate
  )}) ${format(eventOpenAtDate, "HH:mm")} / ${format(
    new Date(eventStartAt),
    "HH:mm"
  )}`;

  const isPaymentMethodAvailable =
    !isRegisteredCreditCardSelected || creditCard.lastFourDigits.length > 0;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!isAgreed || !isPaymentMethodAvailable) {
      return;
    }

    if (isRegisteredCreditCardSelected) {
      // TODO: リセール申込確定API接続後は、API成功時にだけ完了画面へ遷移する。
      history.push(
        `/events/${eventId}/tickets/${ticketId}/resale/completed${history.location.search}`
      );
      return;
    }

    const formData = new FormData(event.currentTarget);
    const cardTokenPromise = getGMOToken({
      cardNo: String(formData.get("cardNumber") ?? ""),
      expireMonth: String(formData.get("cardExpireMonth") ?? ""),
      expireYear: String(formData.get("cardExpireYear") ?? ""),
      holderName: String(formData.get("cardHolderName") ?? "").toUpperCase(),
      securityCode: String(formData.get("cardSecurityCode") ?? ""),
    });
    event.currentTarget.reset();

    const cardToken = await cardTokenPromise;

    if (cardToken === undefined) {
      return;
    }

    // TODO: リセール申込確定APIへ、cardTokenと申込内容を送信し、成功後に完了画面へ遷移する。
  };

  return (
    <SubContentsLayout>
      <PCView>
        <BreadcrumbNavigation
          breadcrumbs={[
            { label: t("common.routes.home"), link: "/" },
            { label: t("event.resale.entry.eventList"), link: "/events" },
            { label: eventTitle, link: `/events/${eventId}` },
            { label: pageTitle, link: null },
          ]}
        />
      </PCView>
      <SPView>
        <MobileBreadcrumbNavigation
          href={`/events/${eventId}/tickets/${ticketId}/resale`}
          label={pageTitle}
        />
      </SPView>

      <form css={classes.root} onSubmit={handleSubmit}>
        <h1 css={classes.heading}>{pageTitle}</h1>
        <Stepper
          ariaLabel={t("event.resale.entry.steps.ariaLabel")}
          currentStepId="confirmation"
          steps={[
            {
              id: "quantitySelection",
              label: t("event.resale.entry.steps.quantity"),
            },
            {
              id: "confirmation",
              label: t("event.resale.entry.steps.confirmation"),
            },
            {
              id: "completion",
              label: t("event.resale.entry.steps.completion"),
            },
          ]}
        />

        <SectionHeading as="h2" fontSize={18} fontWeight={700}>
          {t("event.resale.entry.confirmation.entryDetailsTitle")}
        </SectionHeading>

        <DetailsTable
          rows={[
            {
              label: t("event.resale.entry.confirmation.performance"),
              value: [
                <span key="eventTitle" {...withNoTranslate()}>
                  {eventTitle}
                </span>,
                <span key="eventDateTime" {...withNoTranslate()}>
                  {eventDateTime}
                </span>,
              ],
            },
            {
              label: t("event.resale.entry.confirmation.ticketType"),
              value: <span {...withNoTranslate()}>{ticketName}</span>,
            },
            {
              label: t("event.resale.entry.confirmation.desiredQuantity"),
              value: t("event.resale.entry.confirmation.desiredQuantityValue", {
                quantity: desiredQuantity,
              }),
            },
          ]}
        />

        <SectionHeading as="h2" fontSize={18} fontWeight={700}>
          {t("event.resale.entry.confirmation.paymentAmountTitle")}
        </SectionHeading>

        <div css={classes.paymentAmount}>
          <AmountSummaryTable
            rows={[
              {
                label: t("event.resale.entry.confirmation.ticketPrice", {
                  quantity: desiredQuantity,
                  ticketPrice: `¥${ticketPriceAmount.toLocaleString()}`,
                }),
                amount: ticketPriceAmount * desiredQuantity,
              },
              {
                label: t("event.resale.entry.confirmation.purchaseFee"),
                amount: purchaseFeeAmount,
              },
            ]}
            subText={t(
              "event.resale.entry.confirmation.totalPaymentAmountSubText"
            )}
          />
          <p css={classes.paymentTimingNote}>
            {t("event.resale.entry.confirmation.paymentTimingNote")}
          </p>
        </div>

        <SectionHeading as="h2" fontSize={18} fontWeight={700}>
          {t("event.resale.entry.confirmation.paymentMethodTitle")}
        </SectionHeading>

        <ResaleApplicationPaymentMethod
          creditCard={creditCard}
          isRegisteredCreditCardSelected={isRegisteredCreditCardSelected}
          setIsRegisteredCreditCardSelected={setIsRegisteredCreditCardSelected}
        />

        <AgreementCheckbox isAgreed={isAgreed} onChange={setIsAgreed} />
        <ResaleApplicationSubmissionAction
          isAgreed={isAgreed}
          isCreditCardRegistered={isPaymentMethodAvailable}
        />
      </form>
    </SubContentsLayout>
  );
};

const styles = () => ({
  root: css`
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 92%;
    max-width: 760px;
    padding: 24px 0 0;
    margin: 0 auto;
    @media screen and (min-width: 768px) {
      gap: 32px;
      padding-top: 40px;
    }
  `,
  heading: css`
    font-size: 18px;
    font-weight: bold;
    line-height: 1.5;
    @media screen and (min-width: 768px) {
      font-size: 24px;
    }
  `,
  paymentAmount: css`
    display: grid;
    gap: 16px;
  `,
  paymentTimingNote: css`
    margin: 0;
    color: #5c5c5c;
    font-size: 12px;
    line-height: 1.7;
  `,
});
